import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  subscriptionPlansService,
  SubscriptionPlanCreateData,
  SubscriptionPlanUpdateData,
  SubscriptionPlanFilters,
  SubscriptionPlanSortOptions,
  SubscriptionPlanPagination,
} from '@/services/billing/subscription-plans.service';
import { toast } from 'sonner';
import { SubscriptionPlan, TranslatedSubscriptionPlan, TranslationParams } from '@/types/billing';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object') {
    
    // Check for JSON:API error format
    const data = error.response.data as Record<string, unknown>;
    if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
      const firstError = data.errors[0] as Record<string, unknown>;
      if ('detail' in firstError && typeof firstError.detail === 'string') {
        return firstError.detail;
      }
    }
    
    // Fallback to message field
    if ('message' in data && typeof data.message === 'string') {
      return data.message;
    }
  }
  return defaultMessage;
}

// Query Keys
export const subscriptionPlanKeys = {
  all: ['subscription-plans'] as const,
  lists: () => [...subscriptionPlanKeys.all, 'list'] as const,
  list: (
    productId: string,
    filters?: SubscriptionPlanFilters,
    sort?: SubscriptionPlanSortOptions,
    translationParams?: TranslationParams,
    pagination?: SubscriptionPlanPagination
  ) => [...subscriptionPlanKeys.lists(), productId, filters, sort, translationParams, pagination] as const,
  details: () => [...subscriptionPlanKeys.all, 'detail'] as const,
  detail: (productId: string, planId: string, translationParams?: TranslationParams) => 
    [...subscriptionPlanKeys.details(), productId, planId, translationParams] as const,
};

/**
 * Hook to fetch all subscription plans for a product
 */
export function useSubscriptionPlans(
  productId: string,
  filters?: SubscriptionPlanFilters,
  sort?: SubscriptionPlanSortOptions,
  include?: string[],
  translationParams?: TranslationParams,
  pagination?: SubscriptionPlanPagination,
  options?: { enabled?: boolean }
) {
  return useQuery<TranslatedSubscriptionPlan[]>({
    queryKey: subscriptionPlanKeys.list(productId, filters, sort, translationParams, pagination),
    queryFn: () =>
      subscriptionPlansService.getSubscriptionPlans(
        productId,
        filters,
        sort,
        include,
        translationParams,
        pagination
      ),
    enabled: !!productId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single subscription plan
 */
export function useSubscriptionPlan(
  productId: string, 
  planId: string,
  translationParams?: TranslationParams
) {
  return useQuery<TranslatedSubscriptionPlan>({
    queryKey: subscriptionPlanKeys.detail(productId, planId, translationParams),
    queryFn: () => subscriptionPlansService.getSubscriptionPlan(productId, planId, translationParams),
    enabled: !!productId && !!planId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new subscription plan
 */
export function useCreateSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: SubscriptionPlanCreateData }) =>
      subscriptionPlansService.createSubscriptionPlan(productId, data),
    onSuccess: (newPlan, { productId }) => {
      // Add the new plan to the list cache
      queryClient.setQueryData(
        subscriptionPlanKeys.list(productId),
        (oldData: SubscriptionPlan[] | undefined) => {
          if (!oldData) return [newPlan];
          return [...oldData, newPlan];
        }
      );
      
      // Set the detail cache for the new plan
      queryClient.setQueryData(
        subscriptionPlanKeys.detail(productId, newPlan.id),
        newPlan
      );
      
      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: subscriptionPlanKeys.lists() });
    },
  });
}

/**
 * Hook to update a subscription plan (full update)
 */
export function useUpdateSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      planId, 
      data 
    }: { 
      productId: string; 
      planId: string; 
      data: SubscriptionPlanUpdateData 
    }) =>
      subscriptionPlansService.updateSubscriptionPlan(productId, planId, data),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        subscriptionPlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Update the plan in any list cache (filters/translations vary per list)
      queryClient.setQueriesData(
        { queryKey: subscriptionPlanKeys.lists() },
        (oldData: SubscriptionPlan[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((plan) => (plan.id === planId ? updatedPlan : plan));
        }
      );
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: subscriptionPlanKeys.lists() });
    },
  });
}

/**
 * Hook to partially update a subscription plan
 */
export function usePatchSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      planId, 
      data 
    }: { 
      productId: string; 
      planId: string; 
      data: Partial<SubscriptionPlanUpdateData> 
    }) =>
      subscriptionPlansService.patchSubscriptionPlan(productId, planId, data),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        subscriptionPlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Update the plan in list cache
      queryClient.setQueryData(
        subscriptionPlanKeys.list(productId),
        (oldData: SubscriptionPlan[] | undefined) => {
          if (!oldData) return [updatedPlan];
          return oldData.map(plan => plan.id === planId ? updatedPlan : plan);
        }
      );
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: subscriptionPlanKeys.lists() });
      
      toast.success('Subscription plan updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update subscription plan'));
    },
  });
}

/**
 * Hook to delete a subscription plan
 */
export function useDeleteSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, planId }: { productId: string; planId: string }) =>
      subscriptionPlansService.deleteSubscriptionPlan(productId, planId),
    onSuccess: (_, { productId, planId }) => {
      // Remove from list cache
      queryClient.setQueryData(
        subscriptionPlanKeys.list(productId),
        (oldData: SubscriptionPlan[] | undefined) => {
          if (!oldData) return [];
          return oldData.filter(plan => plan.id !== planId);
        }
      );
      
      // Remove detail cache
      queryClient.removeQueries({ queryKey: subscriptionPlanKeys.detail(productId, planId) });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: subscriptionPlanKeys.lists() });
      
      toast.success('Subscription plan deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete subscription plan. Please archive it instead if there are active subscriptions.'));
    },
  });
}

/**
 * Hook to archive a subscription plan
 */
export function useArchiveSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, planId }: { productId: string; planId: string }) =>
      subscriptionPlansService.archiveSubscriptionPlan(productId, planId),
    onSuccess: (updatedPlan, { productId, planId }) => {
      // Update the detail cache
      queryClient.setQueryData(
        subscriptionPlanKeys.detail(productId, planId),
        updatedPlan
      );
      
      // Update the plan in list cache
      queryClient.setQueryData(
        subscriptionPlanKeys.list(productId),
        (oldData: SubscriptionPlan[] | undefined) => {
          if (!oldData) return [updatedPlan];
          return oldData.map(plan => plan.id === planId ? updatedPlan : plan);
        }
      );
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: subscriptionPlanKeys.lists() });
      
      toast.success('Subscription plan archived successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to archive subscription plan'));
    },
  });
}

/**
 * Prefetch a subscription plan for hover/preview scenarios
 */
export function usePrefetchSubscriptionPlan() {
  const queryClient = useQueryClient();

  return (productId: string, planId: string, translationParams?: TranslationParams) => {
    queryClient.prefetchQuery({
      queryKey: subscriptionPlanKeys.detail(productId, planId, translationParams),
      queryFn: () => subscriptionPlansService.getSubscriptionPlan(productId, planId, translationParams),
      staleTime: 5 * 60 * 1000,
    });
  };
}
